import React, { useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useCall } from '../../context/CallContext';

/**
 * Incoming Call Modal - Shows when receiving a call
 * Works for calls from both iOS and Web users
 */
export default function IncomingCallModal() {
  const { incomingCall, callStatus, acceptCall, rejectCall, stopRinging } = useCall();

  // Listen for call cancellation (when caller hangs up before we answer)
  useEffect(() => {
    if (!incomingCall?.id || callStatus !== 'ringing') return;

    console.log('📞 Listening for call status changes:', incomingCall.id);

    const unsubscribe = onSnapshot(
      doc(db, 'calls', incomingCall.id),
      (snapshot) => {
        const data = snapshot.data();
        if (!data) {
          console.log('📞 Call document deleted - caller cancelled');
          stopRinging?.();
          return;
        }

        console.log('📞 Call status changed:', data.status);

        // If caller cancelled or ended the call before we answered
        if (data.status === 'ended' || data.status === 'cancelled' || data.status === 'missed') {
          console.log('📞 Caller hung up - stopping ring');
          stopRinging?.();
        }
      },
      (error) => {
        console.error('Call listener error:', error);
      }
    );

    return () => {
      console.log('📞 Cleaning up call listener');
      unsubscribe();
    };
  }, [incomingCall?.id, callStatus, stopRinging]);

  // Only show when there's an incoming call
  if (!incomingCall || callStatus !== 'ringing') {
    return null;
  }

  const isVideoCall = incomingCall.type === 'video';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-gray-900 rounded-2xl p-8 max-w-sm w-full mx-4 shadow-2xl border border-gray-700">
        {/* Caller Info */}
        <div className="text-center mb-8">
          {/* Avatar */}
          <div className="relative inline-block mb-4">
            {incomingCall.callerPhotoUrl ? (
              <img
                src={incomingCall.callerPhotoUrl}
                alt={incomingCall.callerName}
                className="w-24 h-24 rounded-full object-cover border-4 border-green-500 animate-pulse"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center border-4 border-green-500 animate-pulse">
                <span className="text-3xl font-bold text-white">
                  {incomingCall.callerName?.charAt(0)?.toUpperCase() || '?'}
                </span>
              </div>
            )}
            {/* Call type indicator */}
            <div className="absolute -bottom-1 -right-1 bg-gray-800 rounded-full p-2">
              {isVideoCall ? (
                <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              )}
            </div>
          </div>

          {/* Caller name */}
          <h2 className="text-xl font-semibold text-white mb-1">
            {incomingCall.callerName || 'Unknown Caller'}
          </h2>

          {/* Call type */}
          <p className="text-gray-400">
            Incoming {isVideoCall ? 'Video' : 'Audio'} Call
          </p>

          {/* Ringing animation */}
          <div className="flex justify-center gap-1 mt-4">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <span className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <span className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center gap-6">
          {/* Decline */}
          <button
            onClick={rejectCall}
            className="w-16 h-16 rounded-full bg-red-600 hover:bg-red-700 flex items-center justify-center transition-all hover:scale-110 shadow-lg"
            title="Decline"
          >
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Accept */}
          <button
            onClick={acceptCall}
            className="w-16 h-16 rounded-full bg-green-600 hover:bg-green-700 flex items-center justify-center transition-all hover:scale-110 shadow-lg"
            title="Accept"
          >
            {isVideoCall ? (
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            ) : (
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
